/**
 * CampaignRepliesDialog — what's behind the campaign page's Replies and
 * Meetings cards (owner request 2026-09-22). Lists the leads that replied
 * (or booked), each with their latest message, and opens the conversation
 * in the inbox. Backed by GET /campaigns/:id/replies, which uses the same
 * status sets as the card counts so the list always matches the number.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useApi } from '@/hooks/use-api'
import { fmtDistanceToNow } from '@/lib/dates'

export type RepliesKind = 'replied' | 'meetings'

type ReplyRow = {
  readonly id: string
  readonly full_name: string | null
  readonly first_name: string | null
  readonly last_name: string | null
  readonly company: string | null
  readonly job_title: string | null
  readonly linkedin_url: string | null
  readonly status: string
  readonly last_reply: string | null
  readonly last_reply_at: string | null
  readonly reply_count: number
}

const COPY: Record<RepliesKind, { title: string; empty: string; describe: (n: number) => string }> = {
  replied: {
    title: 'Replies',
    empty: 'No replies in this campaign yet.',
    describe: (n) => `${n} lead${n === 1 ? '' : 's'} replied. Open any conversation to read the full thread or answer.`,
  },
  meetings: {
    title: 'Meetings',
    empty: 'No meetings booked in this campaign yet.',
    describe: (n) => `${n} lead${n === 1 ? '' : 's'} booked a meeting.`,
  },
}

export function CampaignRepliesDialog({
  campaignId,
  kind,
  open,
  onOpenChange,
}: {
  readonly campaignId: string
  readonly kind: RepliesKind
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}) {
  const apiFetch = useApi()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['campaigns', campaignId, 'replies', kind] as const,
    queryFn: async (): Promise<readonly ReplyRow[]> => {
      const res = await apiFetch<{ kind: RepliesKind; leads: readonly ReplyRow[] }>(
        `/api/campaigns/${campaignId}/replies?kind=${kind}`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load replies')
      return res.data.leads
    },
    enabled: open,
    staleTime: 30_000,
  })

  const rows = data ?? []
  const copy = COPY[kind]

  function openConversation(leadId: string) {
    onOpenChange(false)
    navigate(`/inbox?lead=${encodeURIComponent(leadId)}`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>
            {isLoading ? 'Loading…' : copy.describe(rows.length)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load'}
            </p>
          )}
          {!isLoading && !error && rows.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">{copy.empty}</p>
          )}
          {rows.map((r) => {
            const name = r.full_name ?? ([r.first_name, r.last_name].filter(Boolean).join(' ') || 'Unknown')
            return (
              <div key={r.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{name}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {r.status.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {[r.job_title, r.company].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openConversation(r.id)}>
                    Open conversation
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>
                {r.last_reply ? (
                  <div className="mt-2 rounded-md bg-muted/40 px-3 py-2">
                    <p className="text-sm whitespace-pre-wrap line-clamp-4">{r.last_reply}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {r.last_reply_at ? fmtDistanceToNow(r.last_reply_at, { addSuffix: true }) : ''}
                      {r.reply_count > 1 ? ` · ${r.reply_count} replies in total` : ''}
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">
                    The reply text isn&rsquo;t stored here yet. Open the conversation to read it.
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
